'use client';

import { forwardRef, useState, type CSSProperties, type ReactNode } from 'react';
import { SField, type SFieldSize, type SFieldAddonAlign, type SFieldStatus } from '../SField';
import { SGhostButton } from '../SGhostButton';
import { type SIconName } from '../SIcon';
import { cn } from '../../lib/cn';
import { AUTOFILL_RESET_CLASS } from '../../lib/autofill';
import { type SColor } from '../../lib/color';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { type STooltipProps } from '../STooltip';

export type SBarcodeInputSize = SFieldSize;

const FONT: Record<SBarcodeInputSize, { fontSize: number; paddingX: string; gap: string }> = {
  sm: {
    fontSize: 12,
    paddingX: 'var(--cmp-textinput-sm-paddingX)',
    gap: 'var(--cmp-textinput-sm-gap)',
  },
  md: {
    fontSize: 14,
    paddingX: 'var(--cmp-textinput-md-paddingX)',
    gap: 'var(--cmp-textinput-md-gap)',
  },
};

export interface SBarcodeInputProps {
  value?: string | number | null;
  /** 값 변경 (sdUpdate) */
  onValueChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  size?: SBarcodeInputSize;
  clearable?: boolean;
  autoFocus?: boolean;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  rules?: Rule[];
  status?: SFieldStatus;
  focused?: boolean;
  hovered?: boolean;
  /** 입력 앞 슬롯 */
  prefix?: ReactNode;
  /** 입력 뒤 슬롯 */
  suffix?: ReactNode;
  inputClass?: string;
  inputStyle?: CSSProperties;
  // ── SField ──
  label?: string;
  labelWidth?: number | string;
  icon?: SIconName;
  iconColor?: SColor;
  labelTooltip?: string;
  labelTooltipProps?: Partial<STooltipProps>;
  addonLabel?: string;
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  width?: number | string;
  className?: string;
  style?: CSSProperties;
}

/** SBarcodeInput — sd-barcode-input 포팅. 바코드용 노란 배경 입력 + prefix/suffix + clearable. */
export const SBarcodeInput = forwardRef<HTMLInputElement, SBarcodeInputProps>(
  function SBarcodeInput(
    {
      value,
      onValueChange,
      onFocus,
      onBlur,
      size = 'sm',
      clearable = false,
      autoFocus,
      name,
      placeholder = '입력해 주세요.',
      disabled = false,
      readOnly = false,
      rules,
      status,
      focused,
      hovered,
      prefix,
      suffix,
      inputClass,
      inputStyle,
      label,
      labelWidth,
      icon,
      iconColor,
      labelTooltip,
      labelTooltipProps,
      addonLabel,
      addonAlign,
      hint,
      error,
      errorMessage,
      width,
      className,
      style,
    },
    ref,
  ) {
    const f = FONT[size];
    const [ruleError, setRuleError] = useState('');
    const hasValue = value !== undefined && value !== null && String(value) !== '';

    // SForm 등록 — submit 시 폼이 이 필드를 일괄 검증한다 (폼 밖이면 no-op)
    useFormField({
      name,
      validate: () => {
        const msg = runRules(value, rules);
        setRuleError(msg);
        return msg === '';
      },
      resetValidation: () => setRuleError(''),
    });

    const resolvedError = error || ruleError !== '';
    const resolvedStatus =
      status ?? (rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined);

    return (
      <SField
        label={label}
        labelWidth={labelWidth}
        icon={icon}
        iconColor={iconColor}
        labelTooltip={labelTooltip}
        labelTooltipProps={labelTooltipProps}
        addonLabel={addonLabel}
        addonAlign={addonAlign}
        size={size}
        hint={hint}
        error={resolvedError}
        status={resolvedStatus}
        focused={focused}
        hovered={hovered}
        errorMessage={ruleError !== '' ? ruleError : errorMessage}
        width={width}
        disabled={disabled}
        readOnly={readOnly}
        className={className}
        // 바코드 배경색 override
        style={{
          ['--sys-color-field-bg-default' as string]: 'var(--cmp-textinput-bg-barcode)',
          ...style,
        }}
      >
        <label
          className="flex h-full w-full items-center"
          style={{ gap: f.gap, padding: `0 ${f.paddingX}` }}
        >
          {prefix}
          <input
            ref={ref}
            name={name}
            type="text"
            value={value ?? ''}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            autoFocus={autoFocus}
            onChange={e => {
              onValueChange?.(e.target.value);
              if (ruleError !== '') setRuleError(runRules(e.target.value, rules));
            }}
            onFocus={() => onFocus?.()}
            onBlur={e => {
              if (rules && rules.length > 0) setRuleError(runRules(e.target.value, rules));
              onBlur?.();
            }}
            className={cn(
              'h-full min-w-0 flex-1 border-0 bg-transparent p-0 outline-none placeholder:text-[color:var(--cmp-textinput-text-placeholder)] disabled:cursor-not-allowed',
              AUTOFILL_RESET_CLASS,
              inputClass,
            )}
            style={{
              fontSize: f.fontSize,
              color: disabled
                ? 'var(--cmp-textinput-text-disabled)'
                : 'var(--cmp-textinput-text-default)',
              ...inputStyle,
            }}
          />
          {suffix}
          {clearable && hasValue && !disabled && (
            <SGhostButton
              icon="close"
              size="xxs"
              ariaLabel="clear"
              onClick={() => {
                onValueChange?.('');
                setRuleError(runRules('', rules));
              }}
            />
          )}
        </label>
      </SField>
    );
  },
);
